<template>
  <div>
    <div class="space-y-4">
      <div
        v-for="(offer, idx) in croppedOffers"
        :key="offer.id"
        :data-offer-id="offer.id"
      >
        <SearchResultCard
          :id="offer.id"
          :offer-idx="idx + startIdx"
          :offer="offer"
          :is-recommended-sorting-mode="isRecommendedSortingMode"
          :ranking-tags="rankingTagsForOffer(offer.id)"
          :is-favorite="favoriteHashes.has(offer.$meta?.hash ?? '')"
          :is-favorite-disabled="!isAuthorized"
          :is-multi-trip-search="isMultiTripSearch"
          @click="onOfferSelect(offer, idx)"
          @onCashbackInfoClick="onCashbackInfoClick"
          @onFavoriteToggle="toggleOfferFavorite(offer)"
        />
        <IntersectionPoint
          v-if="isIntersectionPoint(idx)"
          @onIntersect="handleIntersect(idx)"
        />
      </div>
    </div>
    <CashbackBottomSheet :name="CASHBACK_BOTTOMSHEET" />
    <FavoriteAddedAlert v-if="!isMultiTripSearch" ref="favoriteAddedAlertRef" />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch, nextTick, useTemplateRef } from 'vue';
  import { controllerModel, IntersectionPoint, useVisibleOffers } from 'shared';
  import type { Dict } from '@aviata/platform-types';
  import { CashbackBottomSheet, type flightEntity } from 'freedom-shared';

  import { SearchResultCard } from '@/widgets/search-result-card';

  import {
    FavoriteAddedAlert,
    FAVORITE_WATCHLIST_KIND,
    useWatchlist,
  } from '@/features/favorites';
  import type { OfferRankingEntry } from '@/features/search-tickets';

  import { analyticsEventsLib, amplitudeEventsLib } from '@/shared/lib';

  import { CASHBACK_BOTTOMSHEET, OFFERS_TO_SHOW } from '../config';

  const bottomSheet = controllerModel.useBottomSheet();

  const props = defineProps<{
    startIdx: number;
    offersList: flightEntity.types.MappedOffer[];
    dict: Dict.Entity;
    isRecommendedSortingMode: boolean;
    useRankingOfferLabels?: boolean;
    ranking?: OfferRankingEntry[];
    offerId?: string;
    searchQuery: string;
    isMultiTripSearch?: boolean;
  }>();

  const emit = defineEmits<{
    onOfferSelect: [offer: flightEntity.types.MappedOffer, index: number];
    onOffersFilter: [hasSelectedFilters: boolean];
    onScrollToOfferEnd: [];
  }>();

  const { favoriteHashes, toggleOffer, isAuthorized } = useWatchlist();

  const favoriteAddedAlertRef = useTemplateRef<
    InstanceType<typeof FavoriteAddedAlert>
  >('favoriteAddedAlertRef');

  const toggleOfferFavorite = async (offer: flightEntity.types.MappedOffer) => {
    const hash = offer.$meta?.hash;
    if (!hash) return;
    const result = await toggleOffer(
      hash,
      props.searchQuery,
      offer.price.amount,
      offer.price.currency,
    );
    if (result === 'added') {
      amplitudeEventsLib.addToFavorites('serp_page');
      favoriteAddedAlertRef.value?.show(FAVORITE_WATCHLIST_KIND.TICKET);
    } else if (result === 'removed') {
      amplitudeEventsLib.removeFromFavorites('serp_page');
    } else if (result === 'limit_reached') {
      favoriteAddedAlertRef.value?.showLimitError();
    }
  };

  const visibleOffers = useVisibleOffers(OFFERS_TO_SHOW);
  const filteredOffers = computed(() => props.offersList);

  const rankingTagsByOfferId = computed(() => {
    if (!props.useRankingOfferLabels || !props.ranking?.length) {
      return null;
    }
    return new Map(props.ranking.map(r => [r.offer_id, r.tags]));
  });

  const rankingTagsForOffer = (offerId: string) => {
    const map = rankingTagsByOfferId.value;
    if (!map) {
      return undefined;
    }
    return map.get(offerId) ?? [];
  };

  const croppedOffers = computed(() =>
    filteredOffers.value.slice(0, visibleOffers.offers.value),
  );

  watch(
    () => props.offerId,
    offerId => {
      if (!offerId) {
        return;
      }

      const idx = props.offersList.findIndex(offer => offer.id === offerId);
      if (idx === -1) {
        return;
      }

      const targetVisibleOffers = Math.min(
        props.offersList.length,
        Math.ceil((idx + 1) / OFFERS_TO_SHOW) * OFFERS_TO_SHOW,
      );

      if (visibleOffers.offers.value < targetVisibleOffers) {
        visibleOffers.offers.value = targetVisibleOffers;
      }

      nextTick(() => {
        const el = document.querySelector(`[data-offer-id="${offerId}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'auto' });
        emit('onScrollToOfferEnd');
      });
    },
  );

  const SCROLL_DEPTHS = [25, 50, 75, 100];

  const scrollDepthIndex = (percent: number) => {
    const total = filteredOffers.value.length;
    if (total === 0) return -1;
    return Math.max(0, Math.floor((total * percent) / 100) - 1);
  };

  const trackedDepths = ref(new Set<number>());
  const trackScrollDepth = (depth: number, offerIdx: number) => {
    if (trackedDepths.value.has(depth)) return;
    trackedDepths.value.add(depth);
    analyticsEventsLib.serpScroll({
      scroll_depth: depth,
      scroll_offers: offerIdx + 1,
    });
  };

  const isIntersectionPoint = (idx: number) => {
    return (
      idx === croppedOffers.value.length - 5 ||
      SCROLL_DEPTHS.some(depth => scrollDepthIndex(depth) === idx)
    );
  };

  const handleIntersect = (idx: number) => {
    if (idx === croppedOffers.value.length - 5) {
      visibleOffers.increaseVisibleOffers(OFFERS_TO_SHOW);
    }
    for (const depth of SCROLL_DEPTHS) {
      if (scrollDepthIndex(depth) === idx) {
        trackScrollDepth(depth, idx);
      }
    }
  };

  const onOfferSelect = (
    offer: flightEntity.types.MappedOffer,
    idx: number,
  ) => {
    emit('onOfferSelect', offer, idx + props.startIdx);
  };

  const onCashbackInfoClick = () => {
    bottomSheet.show(CASHBACK_BOTTOMSHEET);
  };
</script>
